import { Component, ElementRef, HostListener, inject, AfterViewInit, OnDestroy, effect } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { HapticFeedbackDirective } from '../../directives/haptic-feedback/haptic-feedback.directive';

interface NavItem {
  path: string;
  label: string;
  iconPath: string;
  alt: string;
}

@Component({
  selector: 'app-bottom-nav',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, HapticFeedbackDirective],
  templateUrl: './bottom-nav.component.html',
  styleUrl: './bottom-nav.component.scss',
  host: {
    'role': 'tablist',
    'aria-orientation': 'horizontal',
  },
})
export class BottomNavComponent implements AfterViewInit, OnDestroy {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private resizeObserver?: ResizeObserver;
  private measurementInterval?: number;

  readonly router = inject(Router);

  readonly navItems: NavItem[] = [
    { path: '/calendar', label: 'تقویم', iconPath: '/assets/icons/plumpy-calendar.png', alt: 'تقویم' },
    { path: '/symptoms', label: 'علائم', iconPath: '/assets/icons/plumpy-pulse.png', alt: 'علائم' },
    { path: '/dashboard', label: 'خانه', iconPath: '/assets/icons/plumpy-home.png', alt: 'خانه' },
    { path: '/profile', label: 'پروفایل', iconPath: '/assets/icons/plumpy-user.png', alt: 'پروفایل' },
    { path: '/settings', label: 'تنظیمات', iconPath: '/assets/icons/plumpy-settings.png', alt: 'تنظیمات' },
  ];

  constructor() {
    // Set initial fallback height immediately to prevent flash of wrong padding
    document.documentElement.style.setProperty('--bottom-nav-height', '88px');
    
    // Use effect to measure after component is rendered
    effect(() => {
      queueMicrotask(() => this.measureHeight());
    });
  }

  ngAfterViewInit(): void {
    this.measureHeight();
    
    // Observe for size changes
    this.resizeObserver = new ResizeObserver(() => this.measureHeight());
    this.resizeObserver.observe(this.elementRef.nativeElement);
    
    // Fallback: poll for height changes during initial render (handles font loading, etc.)
    this.measurementInterval = window.setInterval(() => {
      this.measureHeight();
    }, 100);
    
    // Stop polling after 3 seconds
    setTimeout(() => {
      if (this.measurementInterval) {
        clearInterval(this.measurementInterval);
      }
    }, 3000);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.measurementInterval) {
      clearInterval(this.measurementInterval);
    }
  }

  private measureHeight(): void {
    const height = this.elementRef.nativeElement.offsetHeight;
    if (height > 0) {
      document.documentElement.style.setProperty('--bottom-nav-height', `${height}px`);
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const activeIndex = this.navItems.findIndex(item => this.router.url === item.path);
    if (activeIndex === -1) return;

    let newIndex = activeIndex;

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        newIndex = (activeIndex + 1) % this.navItems.length;
        break;
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = (activeIndex - 1 + this.navItems.length) % this.navItems.length;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = this.navItems.length - 1;
        break;
    }

    if (newIndex !== activeIndex) {
      this.router.navigateByUrl(this.navItems[newIndex].path);
    }
  }
}
